import type { PregnancyState } from "./PregnancyContext";
import { outcomeHasDeliveryDetails } from "./options";
import { paths } from "@src/packages/openapi/schema";
import { RequestBody } from "@src/packages/openapi/helpers";

type PatchPayload = RequestBody<
  paths["/{tenantId}/patients/{patientId}"]["patch"]
>;

export function transformPregnancyStateToRequest(
  state: PregnancyState,
): PatchPayload {
  // Nothing answered yet
  if (state.hasBeenPregnant === null) {
    return {};
  }

  type PregnancyEntry = {
    id?: string;
    startDate?: string | null;
    outcome?: "miscarriage" | "live_birth" | "still_birth" | "abortion" | null;
    deliveryDate?: string | null;
    deliveryType?: "natural" | "c_section" | null;
    withCurrentPartner?: boolean | null;
    notes?: string | null;
    op?: "delete";
  };

  const pregnanciesPayload: PregnancyEntry[] = [];

  // Deletions
  state.pregnancyIdsToDelete.forEach((id) => {
    if (id) {
      pregnanciesPayload.push({ id, op: "delete" });
    }
  });

  if (state.hasBeenPregnant === true) {
    // Add/update entries
    const deleteIdSet = new Set(state.pregnancyIdsToDelete);
    state.pregnancies
      .filter((p) => !p.id || !deleteIdSet.has(p.id))
      .forEach((p) => {
        const entry: PregnancyEntry = {};
        if (p.id) entry.id = p.id;
        if (p.startDate != null) entry.startDate = p.startDate;
        if (p.outcome != null) entry.outcome = p.outcome;
        if (p.deliveryDate != null) {
          entry.deliveryDate = p.deliveryDate;
        } else if (p.id && !outcomeHasDeliveryDetails(p.outcome)) {
          entry.deliveryDate = null;
        }
        if (p.deliveryType != null) {
          entry.deliveryType = p.deliveryType;
        } else if (p.id && !outcomeHasDeliveryDetails(p.outcome)) {
          entry.deliveryType = null;
        }
        if (p.withCurrentPartner != null) {
          entry.withCurrentPartner = p.withCurrentPartner;
        }
        if (p.notes != null) {
          entry.notes = p.notes;
        }
        pregnanciesPayload.push(entry);
      });
  } else if (state.hasBeenPregnant === false) {
    // Delete all existing when user says "No"
    const deleteIdSet = new Set(state.pregnancyIdsToDelete);
    state.pregnancies.forEach((p) => {
      if (p.id && !deleteIdSet.has(p.id)) {
        pregnanciesPayload.push({ id: p.id, op: "delete" });
      }
    });
  }
  for (let index = 0; index < state.anonymousPregnanciesToDelete; index += 1) {
    // This local marker is assigned the older write's server ID during FIFO
    // reconciliation. It is stripped from the wire if that row was never made.
    pregnanciesPayload.push({ op: "delete" });
  }

  return {
    obGynHistory: {
      hasBeenPregnant: state.hasBeenPregnant,
      ...(pregnanciesPayload.length > 0 && {
        pregnancies: pregnanciesPayload,
      }),
    },
  };
}
